import { useState, useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Eye, EyeOff, KeyRound, Check, X, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';

interface PasswordRule {
  key: string;
  test: (pw: string) => boolean;
}

const PASSWORD_RULES: PasswordRule[] = [
  { key: 'minLength', test: (pw) => pw.length >= 12 },
  { key: 'uppercase', test: (pw) => /[A-Z]/.test(pw) },
  { key: 'lowercase', test: (pw) => /[a-z]/.test(pw) },
  { key: 'number', test: (pw) => /\d/.test(pw) },
];

export function ForceChangePassword() {
  const navigate = useNavigate();
  const { state, logout } = useAuth();
  const { t } = useTranslation('auth');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Redirect if not in password_expired state
  if (state.status === 'authenticated') {
    return <Navigate to="/dashboard" replace />;
  }
  if (state.status !== 'password_expired') {
    return <Navigate to="/login" replace />;
  }

  const tempToken = state.tempToken;

  // Validate password rules
  const ruleResults = useMemo(
    () => PASSWORD_RULES.map((rule) => ({ ...rule, label: t(`passwordRules.${rule.key}`), passed: rule.test(newPassword) })),
    [newPassword, t],
  );

  const allRulesPassed = ruleResults.every((r) => r.passed);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit = allRulesPassed && passwordsMatch && !isLoading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setError('');
    setIsLoading(true);

    try {
      const res = await fetch('/internal/v1/auth/force-change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tempToken}`,
        },
        body: JSON.stringify({ newPassword }),
        credentials: 'include',
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ message: res.statusText }));
        throw new Error(data.message || res.statusText);
      }

      // Password changed successfully
      setSuccess(true);

      // After a short delay, log out and redirect to login so the user can sign in fresh
      setTimeout(async () => {
        await logout();
        navigate('/login');
      }, 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleBackToLogin() {
    await logout();
    navigate('/login');
  }

  return (
    <div className="min-h-dvh animated-gradient-bg relative flex items-start sm:items-center justify-center">
      {/* Top-left: Unifiber logo */}
      <div className="absolute top-4 left-4 sm:top-8 sm:left-10 z-10">
        <img src="/UnifiberLogoWhite.png" alt="Unifiber" className="h-8 sm:h-12 opacity-90" />
      </div>

      {/* Centered glass card */}
      <div className="relative z-10 px-6 pt-20 pb-6 sm:py-12 w-full max-w-md">
        <div className="glass-card-login rounded-2xl p-6 sm:p-10 space-y-6">
          {/* Key icon */}
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
              <KeyRound className="w-8 h-8 text-white" />
            </div>
          </div>

          {/* Header */}
          <div className="text-center">
            <h2 className="text-xl font-bold text-white">{t('forceChangePassword.title')}</h2>
            <p className="text-white/60 mt-2 text-sm leading-relaxed">
              {t('forceChangePassword.subtitle')}
            </p>
          </div>

          {/* Success state */}
          {success ? (
            <div className="space-y-4">
              <div className="px-4 py-3 rounded-lg bg-emerald-500/20 border border-emerald-400/30 text-emerald-200 text-sm font-medium text-center">
                {t('forceChangePassword.success')}
              </div>
              <div className="flex justify-center py-2">
                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              </div>
            </div>
          ) : (
            <>
              {/* Change password form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-medium">
                    {error}
                  </div>
                )}

                {/* New password */}
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-white/80 mb-1.5">
                    {t('forceChangePassword.newPasswordLabel')}
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      id="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t('forceChangePassword.newPasswordPlaceholder')}
                      disabled={isLoading}
                      autoFocus
                      className="glass-input w-full px-4 py-2.5 rounded-lg text-sm pr-12 text-white placeholder:text-white/50 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Password requirements checklist */}
                {newPassword.length > 0 && (
                  <div className="space-y-1.5 px-1">
                    {ruleResults.map((rule, i) => (
                      <div key={i} className="flex items-center gap-2">
                        {rule.passed ? (
                          <Check className="w-3.5 h-3.5 text-white shrink-0" />
                        ) : (
                          <X className="w-3.5 h-3.5 text-white/30 shrink-0" />
                        )}
                        <span
                          className={`text-xs ${
                            rule.passed ? 'text-white font-medium' : 'text-white/40'
                          }`}
                        >
                          {(rule as any).label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Confirm password */}
                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-white/80 mb-1.5">
                    {t('forceChangePassword.confirmLabel')}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirm-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t('forceChangePassword.confirmPlaceholder')}
                      disabled={isLoading}
                      className="glass-input w-full px-4 py-2.5 rounded-lg text-sm pr-12 text-white placeholder:text-white/50 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && !passwordsMatch && (
                    <p className="mt-1.5 text-xs text-red-300/80">{t('forceChangePassword.passwordsDoNotMatch', { defaultValue: 'Passwords do not match' })}</p>
                  )}
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose hover:-translate-y-px hover:shadow-[0_8px_25px_rgba(237,18,95,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
                >
                  {isLoading && (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  )}
                  {isLoading ? t('forceChangePassword.submitting') : t('forceChangePassword.submit')}
                </button>
              </form>

              {/* Back to login */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:text-white bg-white/10 border border-white/[0.28] backdrop-blur-[10px] hover:bg-white/[0.18] hover:border-white/40 hover:-translate-y-px transition-all duration-300"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('forceChangePassword.backToLogin')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom copyright */}
      <div className="absolute bottom-6 left-0 right-0 text-center z-10">
        <p className="text-white/25 text-xs">&copy; 2026 Unifiber Belgium</p>
      </div>
    </div>
  );
}
